import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import AdminLayout from '../../components/admin/AdminLayout';
import Avatar from '../../components/common/Avatar';
import { InlineLoader } from '../../components/common/Loader';
import { getAdminDoctors, verifyDoctor, suspendDoctor } from '../../services/adminService';

export default function AdminDoctors() {
  const [doctors, setDoctors]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [q, setQ]               = useState('');
  const [verifiedFilter, setVerifiedFilter] = useState('all');
  const [acting, setActing]     = useState(null); // id of doctor being acted on

  const load = useCallback(async (pg = 1) => {
    setLoading(true);
    try {
      const params = { page: pg, limit: 20 };
      if (q) params.q = q;
      if (verifiedFilter !== 'all') params.verified = verifiedFilter === 'verified';
      const res = await getAdminDoctors(params);
      if (pg === 1) setDoctors(res.doctors || []);
      else setDoctors(p => [...p, ...(res.doctors || [])]);
      setTotal(res.total || 0);
      setPage(pg);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [q, verifiedFilter]);

  useEffect(() => { load(1); }, [load]);

  const handleVerify = async (id) => {
    setActing(id);
    try {
      await verifyDoctor(id);
      toast.success('Doctor verified!');
      setDoctors(p => p.map(d => d._id === id ? { ...d, isVerified: true } : d));
    } catch (err) { toast.error(err.message); }
    finally { setActing(null); }
  };

  const handleSuspend = async (id, currentlyActive) => {
    const action = currentlyActive ? 'suspend' : 'restore';
    if (!window.confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} this doctor?`)) return;
    setActing(id);
    try {
      await suspendDoctor(id, currentlyActive);
      toast.success(`Doctor ${action}d`);
      setDoctors(p => p.map(d => d._id === id ? { ...d, isActive: !currentlyActive } : d));
    } catch (err) { toast.error(err.message); }
    finally { setActing(null); }
  };

  return (
    <AdminLayout title="Doctors Management">
      <div className="space-y-4">

        {/* Filters */}
        <div className="flex gap-3 flex-wrap items-end">
          <div className="flex-1 min-w-48">
            <label className="input-label">Search</label>
            <div className="relative">
              <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-text-muted text-sm" />
              <input value={q} onChange={e => setQ(e.target.value)}
                placeholder="Name, clinic, phone..."
                className="input pl-9" />
            </div>
          </div>
          <div>
            <label className="input-label">Status</label>
            <select value={verifiedFilter} onChange={e => setVerifiedFilter(e.target.value)} className="input w-40">
              <option value="all">All Doctors</option>
              <option value="verified">Verified</option>
              <option value="unverified">Unverified</option>
            </select>
          </div>
        </div>

        <p className="text-sm text-text-muted">{total} doctor{total !== 1 ? 's' : ''} found</p>

        {/* Doctor cards */}
        {loading && page === 1 ? <InlineLoader text="Loading doctors..." /> : (
          <div className="space-y-2">
            {doctors.length === 0 ? (
              <div className="card-p text-center py-12">
                <i className="fa-solid fa-user-doctor text-3xl text-text-muted mb-2 block" />
                <p className="text-sm text-text-muted">No doctors found</p>
              </div>
            ) : doctors.map(doc => (
              <div key={doc._id} className="card-p flex items-start gap-4">
                <div className="relative flex-shrink-0">
                  <Avatar src={doc.profileImg} name={doc.name} size="md" />
                  {doc.isVerified && (
                    <i className="fa-solid fa-circle-check text-primary text-sm absolute -bottom-0.5 -right-0.5 bg-white rounded-full" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div>
                      <p className="font-semibold text-text-primary">{doc.name}</p>
                      <p className="text-xs text-primary">{doc.speciality || 'No speciality set'}</p>
                      <p className="text-xs text-text-muted">{doc.clinicName} · {doc.phone}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
                      {!doc.isActive && (
                        <span className="badge-danger">Suspended</span>
                      )}
                      {doc.isVerified ? (
                        <span className="badge-success">Verified</span>
                      ) : (
                        <span className="badge-warning">Unverified</span>
                      )}
                    </div>
                  </div>

                  {/* Stats row */}
                  <div className="flex items-center gap-3 mt-2 text-xs text-text-muted">
                    <span><i className="fa-solid fa-star text-amber-400 mr-1" />{Number(doc.rating).toFixed(1)}</span>
                    <span><i className="fa-solid fa-comment mr-1" />{doc.reviewCount} reviews</span>
                    {doc.consultationFee > 0 && <span>₹{doc.consultationFee}/visit</span>}
                    {doc.location?.city && <span><i className="fa-solid fa-location-dot mr-1" />{doc.location.city}</span>}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 mt-3 flex-wrap">
                    {!doc.isVerified && (
                      <button
                        onClick={() => handleVerify(doc._id)}
                        disabled={acting === doc._id}
                        className="px-3 py-1.5 bg-primary text-white text-xs font-semibold rounded-lg flex items-center gap-1.5"
                      >
                        {acting === doc._id
                          ? <i className="fa-solid fa-spinner animate-spin" />
                          : <i className="fa-solid fa-user-check text-[10px]" />}
                        Verify
                      </button>
                    )}
                    <button
                      onClick={() => handleSuspend(doc._id, doc.isActive)}
                      disabled={acting === doc._id}
                      className={`px-3 py-1.5 text-xs font-semibold rounded-lg border flex items-center gap-1.5 ${
                        doc.isActive
                          ? 'border-danger/30 text-danger hover:bg-red-50'
                          : 'border-success/30 text-success hover:bg-green-50'
                      }`}
                    >
                      <i className={`fa-solid ${doc.isActive ? 'fa-ban' : 'fa-rotate-left'} text-[10px]`} />
                      {doc.isActive ? 'Suspend' : 'Restore'}
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {doctors.length < total && (
              <button onClick={() => load(page + 1)} disabled={loading}
                className="w-full py-3 border border-border rounded-btn text-sm font-medium text-primary">
                Load More
              </button>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
